import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-confirmation-modal',
  standalone: true,
  imports: [CommonModule],
  template: `
    <!-- Modal Backdrop -->
    <div *ngIf="showModal" 
         class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9998]" 
         (click)="onCancel()" 
         (keydown)="onKeydown($event)"         
         title="Press Escape or click to close"
         [attr.aria-labelledby]="'confirmation-modal-title-'+_uniqueId">
      <!-- Modal Content -->
      <div class="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 z-[9999]" 
           (click)="$event.stopPropagation()"
           (keydown)="onKeydown($event)"
           [attr.aria-labelledby]="'confirmation-modal-title-'+_uniqueId">
        <!-- Modal Header -->
        <div class="flex justify-between items-center p-6 border-b border-gray-200">
          <h2 [id]="'confirmation-modal-title-'+_uniqueId" class="text-xl font-bold text-gray-900">{{ title }}</h2>
          <button (click)="onCancel()" class="text-gray-400 hover:text-gray-600 transition-colors">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>

        <!-- Modal Body -->
        <div class="p-6">
          <p class="text-gray-600 mb-6">{{ message }}</p>

          <!-- Action Buttons -->
          <div class="flex justify-end gap-3">
            <button
              (click)="onCancel()"
              class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              {{ cancelText }}
            </button>
            <button
              (click)="onConfirm()"
              class="px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-brown-700 transition-colors"
            >
              {{ confirmText }}
            </button>
          </div>
        </div>
      </div>
    </div>
  `,
  styles: [`
    :host {
      display: contents;
    }
  `]
})
export class ConfirmationModalComponent {
  @Input() showModal = false;
  @Input() title = 'Confirm Action';
  @Input() message = 'Are you sure you want to proceed?';
  @Input() confirmText = 'Confirm';
  @Input() cancelText = 'Cancel';
  _uniqueId: string; // Made public for template access
  @Output() confirmed = new EventEmitter<void>();
  @Output() cancelled = new EventEmitter<void>();

  constructor() {
    this._uniqueId = 'modal-' + Math.random().toString(36).slice(2, 11);
  }

  onConfirm(): void {
    this.confirmed.emit();
  }

  onCancel(): void {
    this.cancelled.emit();
  }

  onKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      this.onCancel();
    }
    else if (event.key === 'Enter') {
      // Close only if focused element is NOT an input/textarea
      const tag = (event.target as HTMLElement).tagName.toLowerCase();
      if (tag !== 'input' && tag !== 'textarea') {
        event.preventDefault();
        this.onCancel();
      }
    }
    else if (event.key === ' ' && event.target === event.currentTarget) {
      // Prevent space only when pressed on backdrop
      event.preventDefault();
    }
  }

  onOverlayClick(event: MouseEvent): void {
    this.onCancel(); // or any method to close the modal
  }

  onOverlayKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.onCancel(); // or set this.isVisible = false;
    }
  }

  onContainerKeyDown(event: KeyboardEvent): void {
    // Optional: add logic if needed
    console.info('Key pressed in modal container:', event.key);
  }
  
}
